import React, { useState, useEffect, useMemo } from 'react';
import {
  InputGroup,
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
  Table,
  Modal,
} from 'react-bootstrap';
import axios from 'axios';
import { apiBackendBaseUrl } from 'utils/envsetup';
import { getDefaultReqConfig } from 'request/defaultReqConfig';
import keycloak from 'Keycloak';
import { FaEye } from 'react-icons/fa';
import { FaCheckCircle } from 'react-icons/fa'; // Make sure this import is present
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useSelector } from 'react-redux';

function SearchDomain(props) {
  const [searchInput, setSearchInput] = useState('');
  const [domains, setDomains] = useState([]);
  const [domainDetails, setDomainDetails] = useState({});
  const [filteredStatusDomains, setFilteredStatusDomains] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = useSelector(state => state.commonData?.userProfile?.userId);

  const [analyticsData, setAnalyticsData] = useState({
    COMPLETE: 0,
    DNS_SETUP: 0,
    DOMAIN_PURCHASE: 0,
    DOMAIN_SELECTION: 0,
    DOMAIN_SKIPPED: 4,
    DOMAIN_VERIFICATION: 0,
    Total: 27,
  });
  const [selectedCard, setSelectedCard] = useState(null);
  const getDomainDetails = async () => {
    setLoading(true);
    try {
      const url = `${apiBackendBaseUrl}admin/domain/getdomains`;
      const { data } = await axios.get(url, {
        headers: {
          'X-Data-Token': getDefaultReqConfig(),
          Authorization: `Bearer ${keycloak.token}`,
        },
      });
      if (data) {
        setDomains(data?.tableData);
        setAnalyticsData(data?.analytics);
        setError(null);
      } else {
        setError(data?.errMsg || 'No data found. Please try with a different ID.');
      }
    } catch (error) {
      setError('Error fetching data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDomainDetails();
  }, []);

  const handleNextAction = async draftId => {
    setLoading(true);
    try {
      const url = `${apiBackendBaseUrl}domain/${draftId}/${userId}/{nextaction}/handledomainsetup")`;
      const { data } = await axios.get(url, {
        headers: {
          'X-Data-Token': getDefaultReqConfig(),
          Authorization: `Bearer ${keycloak.token}`,
        },
      });
      if (data) {
        alert(data);
      } else {
        // setError(data?.errMsg || 'No data found. Please try with a different ID.');
      }
    } catch (error) {
      //   setError('Error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async status => {
    setSelectedCard(status);
    try {
      const url = `${apiBackendBaseUrl}admin/domain/getdomaindtlsbystatus/${status}`;
      const { data } = await axios.get(url, {
        headers: {
          'X-Data-Token': getDefaultReqConfig(),
          Authorization: `Bearer ${keycloak.token}`,
        },
      });
      if (data) {
        setDomains(data);
        setFilteredStatusDomains([]);
      } else {
        setDomains([]);
      }
    } catch (err) {
      setFilteredStatusDomains([]);
    }
  };

  const renderAnalyticsCard = (label, value) => (
    <Col md={3} sm={6} xs={12} className="mb-3" key={label}>
      <Card
        className={`shadow-sm animate__animated animate__fadeIn`}
        onClick={() => {
          setSelectedCard(label);
          label === 'Total' ? getDomainDetails() : handleCardClick(label);
        }}
        style={{
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          border: selectedCard === label ? '1px solid #6A2BF3' : '1px solid #dee2e6',
          boxShadow:
            selectedCard === label
              ? '0 6px 20px rgba(106, 43, 243, 0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
          position: 'relative',
        }}
        onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.03)')}
        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
      >
        {selectedCard === label && (
          <FaCheckCircle
            color="#6A2BF3"
            size={20}
            style={{ position: 'absolute', top: 8, right: 8 }}
          />
        )}

        <Card.Body>
          <Card.Title className="text-muted small">{label}</Card.Title>
          <Card.Text className="h5 mb-0">{value}</Card.Text>
        </Card.Body>
      </Card>
    </Col>
  );

  const auditColumns = [
    {
      accessorKey: 'crmId',
      header: 'crmId',
    },
    {
      accessorKey: 'studioLoginId',
      header: 'StudioLoginId',
    },
    {
      accessorKey: 'selectedDomain',
      header: 'Domain Name',
    },
    {
      accessorKey: 'domainCreationDate',
      header: 'Domain Start Date',
    },
    {
      accessorKey: 'domainExpiryDate',
      header: 'Domain Expiry Date',
    },
    {
      accessorKey: 'domainStatus',
      header: 'Domain Status',
    },
  ];

  const filteredDomains = useMemo(() => {
    return (
      (filteredStatusDomains.length > 0 ? filteredStatusDomains : domains)?.filter(item =>
        Object.values(item).some(value =>
          value?.toString().toLowerCase()?.includes(searchInput?.toLowerCase()),
        ),
      ) || []
    );
  }, [domains, filteredStatusDomains, searchInput]);

  const auditTable = useReactTable({
    data: filteredDomains,
    columns: auditColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleView = async draftId => {
    setShowModal(true);
    try {
      const url = `${apiBackendBaseUrl}admin/domain/getdomaininfo/${draftId}`;
      const { data } = await axios.get(url, {
        headers: {
          'X-Data-Token': getDefaultReqConfig(),
          Authorization: `Bearer ${keycloak.token}`,
        },
      });
      if (data) {
        setDomainDetails(data);
        setError(null);
      } else {
        setError(data?.errMsg || 'No data found. Please try with a different ID.');
      }
    } catch (error) {
      setError('Error fetching data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => {
    setShowModal(false);
  };

  const formatDateArray = arr => {
    if (!Array.isArray(arr)) return 'N/A';
    const [y, m, d, hr, min, sec] = arr;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')} ${String(hr).padStart(
      2,
      '0',
    )}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const formattedDetails = {
    ...domainDetails,
    createdDate: formatDateArray(domainDetails.createdDate),
    lastModifiedDate: formatDateArray(domainDetails.lastModifiedDate),
  };

  const allKeys = Object.keys(domainDetails);
  const columns = [[], [], []];
  allKeys.forEach((key, index) => {
    columns[index % 3].push(key);
  });

  return (
    <div>
      <div className="container mt-4" style={{ padding: 0 }}>
        <h4 className="text-center mb-4 mt-4">Domain Management</h4>
        <Row className="mb-4">
          {Object.entries(analyticsData).map(([key, value]) => renderAnalyticsCard(key, value))}
        </Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchInput}
            onChange={e => setSearchInput(e?.target?.value)}
            style={{ width: '250px', marginBottom: '10px' }}
          />
        </div>
        <Table bordered hover responsive className="mt-3 customtable">
          <thead className="table-dark text-center">
            {auditTable.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>{flexRender(header.column.columnDef.header)}</th>
                ))}
                <th>View</th>
                <th>Next Action</th>
              </tr>
            ))}
          </thead>
          <tbody>
            {auditTable.getRowModel()?.rows?.length > 0 ? (
              auditTable.getRowModel()?.rows?.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="text-center small-td-font">
                      {cell.getValue() === true
                        ? 'True'
                        : cell.getValue() === false
                        ? 'False'
                        : cell.getValue() ?? 'N/A'}
                    </td>
                  ))}
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleView(row?.original?.draftId)}
                    >
                      <FaEye color="#6A2BF3" />
                    </Button>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      style={{ background: row?.original?.nextAction ? '#6A2BF3' : '' }}
                      onClick={() => {
                        handleNextAction(row?.original?.draftId);
                      }}
                      disabled={true}
                    >
                      {row?.original?.nextAction}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={auditColumns?.length + 1}>No matching records found.</td>
              </tr>
            )}
          </tbody>
        </Table>
        <div className="d-flex align-items-center gap-2 mt-3">
          <Button
            variant="secondary"
            onClick={() => auditTable.setPageIndex(0)}
            disabled={!auditTable.getCanPreviousPage()}
          >
            ⏮ First
          </Button>
          <Button
            variant="secondary"
            onClick={() => auditTable.previousPage()}
            disabled={!auditTable.getCanPreviousPage()}
          >
            ◀ Prev
          </Button>
          <span>
            Page {auditTable.getState().pagination.pageIndex + 1} of {auditTable.getPageCount()}
          </span>
          <Button
            variant="secondary"
            onClick={() => auditTable.nextPage()}
            disabled={!auditTable.getCanNextPage()}
          >
            Next ▶
          </Button>
          <Button
            variant="secondary"
            onClick={() => auditTable.setPageIndex(auditTable.getPageCount() - 1)}
            disabled={!auditTable.getCanNextPage()}
          >
            ⏭ Last
          </Button>
        </div>
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Domain Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Card className="shadow-sm p-3">
              <Card.Body>
                <Row className="g-4">
                  {columns.map((column, colIndex) => (
                    <Col md={4} key={colIndex}>
                      {column.map(key => (
                        <div key={key} className="profile-item mb-3">
                          <div className="profile-key">{key}</div>
                          <div className="profile-value profile-val-font">
                            {formattedDetails[key] != null ? String(formattedDetails[key]) : 'N/A'}
                          </div>
                        </div>
                      ))}
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Modal.Body>
        </Modal>
        {error && (
          <div className="text-center mt-3">
            <Alert variant="danger" className="d-inline-block w-auto">
              {error}
            </Alert>
          </div>
        )}{' '}
      </div>
    </div>
  );
}

export default SearchDomain;
